import { z } from 'zod/v3';

const InventoryModeEnum = z.enum(['INVENTORY_TRACKING', 'LIMITED_QUANTITY']);

const StateEnum = z.enum(['PRELAUNCH', 'ACTIVE', 'PAUSED', 'DELISTED']);

const NextStateTriggersEnum = z.enum([
  'DATE',
  'OUT_OF_STOCK',
  'OUT_OF_STOCK_AFTER_DATE',
  'READY_TO_BE_SUCCEEDED',
]);

const SalesModesEnum = z.enum(['SELL_BY_UNIT_PRICE_BY_UNIT', 'SELL_BY_UNIT_PRICE_BY_WEIGHT_KG']);

const RestrictionTypeEnum = z.enum(['AGE_OTHER', 'OVER_THE_COUNTER', 'DELIVERY_HANDSHAKE']);

export const offersSearchParamsSchema = z.object({
  activeTriggers: z.array(NextStateTriggersEnum).optional(),
  addOfferModalOpen: z.boolean().optional(),
  autoActionsModalOpen: z.boolean().optional(),
  bulkEditExcludedOfferIds: z.array(z.string()).optional(),
  bulkEditOfferIds: z.array(z.string()).optional(),
  bulkOperationId: z.string().optional(),
  primaryCategoryIds: z.array(z.string()).optional(),
  additionalCategoryIds: z.array(z.string()).optional(),
  copyOfferId: z.string().optional(),
  deleteDiscountsModalOpen: z.boolean().optional(),
  deleteOfferIds: z.array(z.string()).optional(),
  deleteOfferModalOpen: z.boolean().optional(),
  deletePromotionsModalOpen: z.boolean().optional(),
  delistedTriggers: z.array(NextStateTriggersEnum).optional(),
  downloadOffersOperationId: z.string().optional(),
  editOfferId: z.string().optional(),
  groupOffersByProduct: z.boolean().optional(),
  inventoryModes: z.array(InventoryModeEnum).optional(),
  offerProductId: z.string().optional(),
  offerStates: z.array(StateEnum).optional(),
  pausedTriggers: z.array(NextStateTriggersEnum).optional(),
  productFamilyIds: z.array(z.string()).optional(),
  noProductFamily: z.boolean().optional(),
  productBrandIds: z.array(z.string()).optional(),
  noProductBrand: z.boolean().optional(),
  productTagIds: z.array(z.string()).optional(),
  noProductTags: z.boolean().optional(),
  query: z.coerce.string().optional(),
  restrictionTypes: z.array(RestrictionTypeEnum).optional(),
  restrictionAgeMin: z.string().optional(),
  restrictionAgeMax: z.string().optional(),
  noRestrictions: z.boolean().optional(),
  salesModes: z.array(SalesModesEnum).optional(),
  uploadDiscountsModalOpen: z.boolean().optional(),
  uploadOffersModalOpen: z.boolean().optional(),
  uploadPromotionsModalOpen: z.boolean().optional(),
  venueCodes: z.array(z.string()).optional(),
});

export type OffersSearchParamsSchemaType = z.infer<typeof offersSearchParamsSchema>;

export const categoriesSearchParamsSchema = z.object({
  query: z.coerce.string().optional(),
  addCategoryModalOpen: z.boolean().optional(),
  addSubcategoryParentId: z.string().optional(),
  editCategoryId: z.string().optional(),
  editSubcategoryId: z.string().optional(),
  editSubcategoryParentId: z.string().optional(),
  page: z.coerce.number().optional(),
});

export type CategoriesSearchParamsSchemaType = z.infer<typeof categoriesSearchParamsSchema>;

export const assortmentSetsParamsSchema = z.object({
  query: z.coerce.string().optional(),
  addSetModalOpen: z.boolean().optional(),
  addSubsetParentId: z.string().optional(),
  editSetId: z.string().optional(),
  deleteSetId: z.string().optional(),
  venueCodes: z.array(z.string()).optional(),
  downloadSetsOperationId: z.string().optional(),
  updateSetsModalOpen: z.boolean().optional(),
  bulkOperationId: z.string().optional(),
});

export type AssortmentSetsParamsSchemaType = z.infer<typeof assortmentSetsParamsSchema>;

export const assortmentSetDetailsParamsSchema = z.object({
  query: z.coerce.string().optional(),
  page: z.coerce.number().optional(),
  addProductsModalOpen: z.boolean().optional(),
  productFamilyIds: z.array(z.string()).optional(),
  noProductFamily: z.boolean().optional(),
  productBrandIds: z.array(z.string()).optional(),
  noProductBrand: z.boolean().optional(),
  productTagIds: z.array(z.string()).optional(),
  noProductTags: z.boolean().optional(),
  restrictionTypes: z.array(RestrictionTypeEnum).optional(),
  restrictionAgeMin: z.string().optional(),
  restrictionAgeMax: z.string().optional(),
  noRestrictions: z.boolean().optional(),
});

export type AssortmentSetDetailsParamsSchemaType = z.infer<typeof assortmentSetDetailsParamsSchema>;

export const assortmentProductSearchParamsSchema = z.object({
  primaryCategoryIds: z.array(z.string()).optional(),
  additionalCategoryIds: z.array(z.string()).optional(),
  inventoryModes: z.array(InventoryModeEnum).optional(),
  offerStates: z.array(StateEnum).optional(),
  salesModes: z.array(SalesModesEnum).optional(),
  activeTriggers: z.array(NextStateTriggersEnum).optional(),
  pausedTriggers: z.array(NextStateTriggersEnum).optional(),
  delistedTriggers: z.array(NextStateTriggersEnum).optional(),
  query: z.coerce.string().optional(),
  venueCodes: z.array(z.string()).optional(),
  from: z.string().optional(),
  productFamilyIds: z.array(z.string()).optional(),
  noProductFamily: z.boolean().optional(),
  productBrandIds: z.array(z.string()).optional(),
  noProductBrand: z.boolean().optional(),
  productTagIds: z.array(z.string()).optional(),
  noProductTags: z.boolean().optional(),
  restrictionAgeMin: z.string().optional(),
  restrictionAgeMax: z.string().optional(),
  restrictionTypes: z.array(RestrictionTypeEnum).optional(),
  noRestrictions: z.boolean().optional(),
});

const AssortmentProductLandingPageEnum = z.enum(['promotions', 'discount-rules']).optional();
export type AssortmentProductLandingPageEnumType = z.infer<typeof AssortmentProductLandingPageEnum>;

export const assortmentProductRedirectSearchParamsSchema = z.object({
  productCode: z.string().optional(),
  from: z.string().optional(),
  landingPage: AssortmentProductLandingPageEnum,
});
